import {
  type AccountInfo,
  type AuthenticationResult,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  AzureAuthority,
  AzureClientId,
  AzurePostLogoutRedirectUri,
  AzureRedirectUri,
  AzureScopes,
  AzureApiScope,
} from '@src/constants/BaseUrls';

const LAST_AZURE_ACCOUNT_KEY = 'mayaLastAzureAccount';

// Used for the initial login redirect and SSO — requests user identity + Graph consent.
// The resulting id_token carries the user's profile claims (name, email, etc.).
const loginRequest = {
  scopes: AzureScopes.length > 0 ? AzureScopes : ['openid', 'profile', 'email'],
};

// Used exclusively when acquiring tokens for the Flask backend API.
// Produces an access_token whose aud = "api://<clientId>" so the backend
// can validate it.  Never mix this with Graph scopes — each resource must
// be requested in a separate acquireToken call.
const apiTokenRequest = {
  scopes: [AzureApiScope],
};

const getDomainHintFromLoginHint = (
  loginHint?: string | null
): string | undefined => {
  if (!loginHint || !loginHint.includes('@')) {
    return undefined;
  }

  const [, domain] = loginHint.split('@');
  return domain || undefined;
};

const getAuthHints = (
  loginHint?: string | null,
  account?: AccountInfo | null
) => {
  const accountClaims = (account?.idTokenClaims || {}) as Record<
    string,
    unknown
  >;
  const accountLoginHint =
    (typeof account?.username === 'string' && account.username) ||
    (typeof accountClaims.preferred_username === 'string'
      ? accountClaims.preferred_username
      : undefined) ||
    (typeof accountClaims.upn === 'string' ? accountClaims.upn : undefined);
  const effectiveLoginHint = loginHint || accountLoginHint || null;
  const sid =
    typeof accountClaims.sid === 'string' ? accountClaims.sid : undefined;
  const domainHint =
    getDomainHintFromLoginHint(effectiveLoginHint) || 'organizations';

  return {
    ...(effectiveLoginHint ? { loginHint: effectiveLoginHint } : {}),
    ...(sid ? { sid } : {}),
    domainHint,
  };
};

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: AzureClientId,
    authority: AzureAuthority,
    navigateToLoginRequestUrl: false,
    ...(AzureRedirectUri ? { redirectUri: AzureRedirectUri } : {}),
    ...(AzurePostLogoutRedirectUri
      ? { postLogoutRedirectUri: AzurePostLogoutRedirectUri }
      : {}),
  },
  cache: {
    // sessionStorage is safer than localStorage (tokens not shared across tabs)
    // and still persists across page reloads within the same browser tab.
    cacheLocation: 'sessionStorage',
  },
});

let msalInitialized = false;
let redirectPromise: Promise<AuthenticationResult | null> | null = null;

const persistPreferredAccount = (account: AccountInfo | null) => {
  if (typeof window === 'undefined' || !account) {
    return;
  }

  msalInstance.setActiveAccount(account);
  window.localStorage.setItem(
    LAST_AZURE_ACCOUNT_KEY,
    account.homeAccountId || account.username
  );
};

const getPreferredAccount = (): AccountInfo | null => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null;
  }

  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  if (typeof window === 'undefined') {
    return accounts[0];
  }

  const storedAccountId = window.localStorage.getItem(LAST_AZURE_ACCOUNT_KEY);
  if (storedAccountId) {
    const matchingAccount = accounts.find(
      (account) =>
        account.homeAccountId === storedAccountId ||
        account.username === storedAccountId
    );

    if (matchingAccount) {
      msalInstance.setActiveAccount(matchingAccount);
      return matchingAccount;
    }
  }

  msalInstance.setActiveAccount(accounts[0]);
  return accounts[0];
};

const initializeMsal = async (): Promise<AuthenticationResult | null> => {
  if (!AzureClientId) {
    throw new Error('Missing AZURE_CLIENT_ID configuration.');
  }

  if (!msalInitialized) {
    await msalInstance.initialize();
    msalInitialized = true;
  }

  if (!redirectPromise) {
    redirectPromise = msalInstance.handleRedirectPromise();
  }

  try {
    const result = await redirectPromise;

    if (result?.account) {
      persistPreferredAccount(result.account);
    }

    return result;
  } finally {
    redirectPromise = null;
  }
};

const acquireTokenSilently = async (
  forceRefresh: boolean = false
): Promise<AuthenticationResult | null> => {
  const account = getPreferredAccount();
  if (!account) {
    return null;
  }

  try {
    const tokenResult = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest, // ← API scope: aud = "api://<clientId>" for Flask backend
      account,
      forceRefresh,
    });

    persistPreferredAccount(tokenResult.account || account);
    return tokenResult;
  } catch (error) {
    if (isInteractionRequiredError(error)) {
      // Token expired or consent is required — return null so caller can
      // fall back to cookie or trigger interactive login.
      return null;
    }
    throw error;
  }
};

const trySilentSingleSignOn = async (
  loginHint?: string | null
): Promise<AuthenticationResult | null> => {
  await initializeMsal();
  const preferredAccount = getPreferredAccount();

  try {
    const ssoResult = await msalInstance.ssoSilent({
      ...loginRequest,
      ...getAuthHints(loginHint, preferredAccount),
    });

    if (ssoResult?.account) {
      persistPreferredAccount(ssoResult.account);
    }

    return ssoResult;
  } catch {
    // Silent SSO can fail when there is no active Microsoft session.
    return null;
  }
};

const loginWithMicrosoft = async (loginHint?: string | null) => {
  await initializeMsal();
  const preferredAccount = getPreferredAccount();
  await msalInstance.loginRedirect({
    ...loginRequest,
    ...getAuthHints(loginHint, preferredAccount),
  });
};

const logoutFromMicrosoft = async () => {
  await initializeMsal();
  const account = getPreferredAccount();
  await msalInstance.logoutRedirect(account ? { account } : undefined);
};

const hasMicrosoftAccount = (): boolean => {
  // Avoid calling MSAL APIs before initialize() completes.
  if (!msalInitialized) {
    return false;
  }

  try {
    return msalInstance.getAllAccounts().length > 0;
  } catch {
    return false;
  }
};

const isInteractionRequiredError = (error: unknown): boolean => {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }

  const errorCode =
    typeof error === 'object' && error
      ? (error as Record<string, unknown>).errorCode
      : null;

  return [
    'interaction_required',
    'login_required',
    'consent_required',
  ].includes(typeof errorCode === 'string' ? errorCode : '');
};

export {
  msalInstance,
  acquireTokenSilently,
  hasMicrosoftAccount,
  initializeMsal,
  isInteractionRequiredError,
  loginWithMicrosoft,
  logoutFromMicrosoft,
  trySilentSingleSignOn,
};
